import React, { useState, useEffect } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNotifications } from './notifications';

const DialogCreateUser = ({ isOpen, onClose }) => {
  // Initialize form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // State to track form validity
  const [formIsValid, setFormIsValid] = useState(false);

  // Helper function to handle field changes
  const handleFieldChange = (field, value) => {
    // Update the appropriate field
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      default:
        break;
    }
  };

  // Reset form fields when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setFormIsValid(false);
    }
  }, [isOpen]);

  const { showSuccess, showError, NotificationComponent } = useNotifications();

  // Check form validity whenever fields change
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const isValid =
      name.trim() !== "" &&
      email.trim() !== "" &&
      emailRegex.test(email) &&
      phone.trim() !== "" &&
      password !== "" &&
      password.length >= 8 &&
      password === confirmPassword;

    setFormIsValid(isValid);
  }, [name, email, phone, password, confirmPassword]);

  const validateForm = () => {
    // Name validation
    if (!name.trim()) {
      showError('Please enter your name');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      showError('Please enter a valid email address');
      return false;
    }

    // Phone validation
    if (!phone.trim()) {
      showError('Please enter your phone number');
      return false;
    }

    // Password validation
    if (!password) {
      showError('Please enter a password');
      return false;
    }

    if (password.length < 8) {
      showError('Password must be at least 8 characters long');
      return false;
    }

    // Confirm password validation
    if (password !== confirmPassword) {
      showError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Check if email is already in use - this is the first check we perform
      const checkEmailResponse = await fetch('/api/user/validate/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email }),
      });

      const checkEmailResult = await checkEmailResponse.json();

      if (checkEmailResult.exists) {
        showError(checkEmailResult.message || 'This email is already registered. Please log in or use a different email.');
        setIsLoading(false);
        return;
      }

      // Create new user
      const userData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password: password
      };

      const response = await fetch('/api/user/save', {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();

      if (result.success) {
        showSuccess(result.message || 'User created successfully! You can now log in.');
        // Add a delay before closing the dialog to allow the user to see the success message
        setTimeout(() => {
          onClose();
        }, 1500); // 1.5 seconds delay
      } else {
        showError(result.message || 'Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showError('An error occurred while creating your user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the form fields on unmount
  useEffect(() => {
    return () => {
      setName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
    };
  }, []);

  const handleClose = () => {
    // Reset all form fields
    setName("");
    setEmail("");
    setPhone("");
    setPassword("");
    setConfirmPassword("");
    // Close the dialog
    onClose();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => { }} // Empty function to prevent auto-closing
        static // Add static prop to prevent closing on outside click
      >
        <NotificationComponent />

        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-4">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Header with border-bottom */}
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Create User
                  </Dialog.Title>
                </div>

                <div className="mt-8 px-4">
                  {/* Name */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="name"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Name <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow">
                        <input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Enter your full name"
                          value={name}
                          onChange={(e) => {
                            handleFieldChange('name', e.target.value);
                          }}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="email"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Email <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => {
                            handleFieldChange('email', e.target.value);
                          }}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="phone"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Phone <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow">
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="Enter your phone number"
                          value={phone}
                          onChange={(e) => {
                            handleFieldChange('phone', e.target.value);
                          }}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="password"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Password <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow flex flex-col">
                        <div className="relative">
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter password"
                            value={password}
                            onChange={(e) => {
                              handleFieldChange('password', e.target.value);
                            }}
                            className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                        {/* Password helper text aligned with input, not affecting eye icon */}
                        {password && password.length < 8 && (
                          <div className="mt-1 text-xs text-red-600 text-left">Password must be at least 8 characters long.</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Confirm Password <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm password"
                          value={confirmPassword}
                          onChange={(e) => {
                            handleFieldChange('confirmPassword', e.target.value);
                          }}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <EyeIcon className="h-5 w-5" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button Section with top border */}
                <div className="border-t border-gray-200 mt-10">
                  <div className="flex flex-col gap-3 mt-5">
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={isLoading}
                        className={`rounded-md bg-gray-200 text-gray-700 py-2 px-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'} focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateUser}
                        disabled={isLoading || !formIsValid}
                        className={`rounded-md ${isLoading || !formIsValid ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"} text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 min-w-[100px]`}
                      >
                        {isLoading ? (
                          <div className="flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Creating...
                          </div>
                        ) : 'Create User'}
                      </button>
                    </div>
                    {/* Divider for Google Sign Up */}
                    <div className="flex items-center my-4">
                      <div className="flex-grow border-t border-gray-300"></div>
                      <span className="mx-4 text-gray-400">or</span>
                      <div className="flex-grow border-t border-gray-300"></div>
                    </div>
                    {/* Google Sign Up Button */}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await fetch('/api/user/google', {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ mode: 'signup' })
                          });
                          if (res.ok) {
                            const data = await res.json();
                            if (data && data.url) {
                              window.location.href = data.url;
                            } else {
                              window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback';
                            }
                          } else {
                            window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback';
                          }
                        } catch {
                          window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback';
                        }
                      }}
                      className="flex items-center justify-center px-4 border border-black rounded-md shadow-sm bg-white hover:bg-gray-100 text-black font-semibold w-full h-11"
                      style={{ gap: '12px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                    >
                      <span className="flex items-center" style={{ minWidth: 20, minHeight: 20 }}>
                        <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_17_40)">
                            <path d="M47.532 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.56 3.02-2.24 5.58-4.78 7.3v6.06h7.72c4.52-4.16 7.09-10.28 7.09-17.676z" fill="#4285F4" />
                            <path d="M24.48 48c6.48 0 11.92-2.14 15.89-5.82l-7.72-6.06c-2.14 1.44-4.88 2.3-8.17 2.3-6.28 0-11.6-4.24-13.5-9.96H2.67v6.24C6.62 43.98 14.88 48 24.48 48z" fill="#34A853" />
                            <path d="M10.98 28.46c-.5-1.44-.78-2.98-.78-4.46s.28-3.02.78-4.46v-6.24H2.67A23.97 23.97 0 000 24c0 3.98.96 7.76 2.67 11.18l8.31-6.72z" fill="#FBBC05" />
                            <path d="M24.48 9.54c3.53 0 6.68 1.22 9.17 3.62l6.87-6.87C36.39 2.14 30.96 0 24.48 0 14.88 0 6.62 4.02 2.67 12.82l8.31 6.24c1.9-5.72 7.22-9.96 13.5-9.96z" fill="#EA4335" />
                          </g>
                          <defs>
                            <clipPath id="clip0_17_40">
                              <rect width="48" height="48" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </span>
                      <span>Sign up with Google</span>
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DialogCreateUser;
